import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import db from '../lib/database';
import { useStore } from '../store/StoreContext';
import { useToast } from '../hooks/useToast';
import { formatTime12 } from '../utils/formatters';
import { IconDollarSign } from '../components/icons';
import { NumberField, Button, IconButton, StatusPill, PageHeader, Toolbar, PeriodStepper, EmptyState, RowSkeleton, pageWrapCls, tableWrapCls, tableCls, thCls, thNumCls, trCls, trClickCls, trOpenCls, tdCls, tdNumCls, tdMutedCls, trExpandCls, tdExpandCls } from '../components/ui';
import {
    payPeriodFor,
    shiftPayPeriod,
    formatPayPeriod,
    payPeriodIsoRange,
    payPeriodSlug,
    splitPayPeriodWeeks,
} from '../lib/payPeriod';

// US FLSA overtime multiplier — anything over 40 hours per week pays
// 1.5× base rate. Hardcoded here; if BBM ever needs a different
// multiplier we can promote it to app_config.
const OT_MULTIPLIER = 1.5;
const OT_WEEKLY_THRESHOLD = 40;

// PayrollPage — admin-only payroll workflow
//
// What this page is for (vs. the Recaps page):
//   • Recaps page = "what happened" — read-only log of submissions
//   • Payroll page = "what to pay" — editable hours, $ calculation,
//                    CSV export tailored for Paylocity ingestion
//
// Same underlying move_jobs recap data, different audience + sensitivity.
// Edits to hours from this page write a PER-CREW override on
// move_jobs.actual_hours_overrides (so deducting Brandon's hour doesn't
// also deduct Will's hour on the same job). The audit log captures the
// jsonb-shape change. Effective hours for a crew member on a job:
//   override?.[crew_id] ?? recap.actual_hours
// Pay math, weekly OT bucketing, and card/check tip splits all use
// effective hours so a per-crew hour change automatically flows through.
//
// Pay-period framing comes from src/lib/payPeriod.js — Sat→Fri × 2 weeks,
// anchor 2026-05-02. Same logic backs the Recaps page presets so the two
// pages stay aligned.
//
// What's deferred:
//   • Approval state per crew per period (just edit + export for now)
//   • Per-crew-per-job hours (currently all crew on a job share actual_hours)
//   • Overtime calculation (>40h/week = 1.5×)
//   • Paylocity API integration (manual CSV upload for now)

// ─── Aggregation: roll recaps up per crew member ─────────────────────────
//
// OT model: per-week, not per-period. The pay period spans two weeks
// (Sat→Fri × 2). For each week, anything over 40h is OT at 1.5×.
// Computing per-week means a 41h+30h split between weeks costs the same
// as 30h+41h (no OT triggered), which matches the US FLSA semantics.
// Hour adjustments stay at the BASE rate — they're corrections, not
// OT-eligible.
//
// Output row shape:
//   {
//     crewId, name, display_color, active, gets_crew_pay,
//     hours_from_recaps,     // sum of actual_hours on jobs they were on
//     hours_adjustment,      // from payroll_adjustments (default 0)
//     total_hours,           // hours_from_recaps + hours_adjustment
//     week1_hours, week2_hours, // recap hours split by week (no adjustment)
//     regular_hours,         // sum of min(40, weekN_hours)
//     ot_hours,              // sum of max(0, weekN_hours − 40)
//     hourly_rate, rate_override, effective_rate,
//     ot_rate,               // effective_rate × OT_MULTIPLIER
//     pay_regular,           // regular_hours × effective_rate
//     pay_ot,                // ot_hours × ot_rate
//     pay_adjustment,        // hours_adjustment × effective_rate (base)
//     pay_from_hours,        // pay_regular + pay_ot + pay_adjustment
//     bonus_total,           // sum of crew_pay_bonus_amount on jobs LED
//     tip_cash, tip_card, tip_check, // per-type tip allocations this period
//     tip_payroll,           // tip_card + tip_check (flows into pay_total)
//     tip_received,          // tip_cash + tip_payroll (informational total)
//     pay_total,             // pay_from_hours + bonus_total + tip_payroll
//     jobs, recaps, adjustment,
//   }
//
// Tip handling rationale:
//   • Card and check tips were processed by the employer, so they're
//     reportable income and need to flow through payroll → pay_total.
//   • Cash tips were handed to the crew on-site, so they're tracked
//     for visibility but excluded from pay_total to avoid double-paying.
// Effective hours that one crew member worked on a given recap row.
// Precedence (sql/052): per-crew override (Payroll page edit) →
// payroll_hours (crew clock time from the recap, when the lead flagged
// "crew time differs from billing") → actual_hours (billed hours — the
// historical behavior and the fallback when crew time wasn't recorded).
// Invoicing reads actual_hours only; this chain is payroll-side.
// Returns a finite non-negative number; treats malformed input as 0.
function effectiveHoursFor(recap, crewId) {
    const overrides = recap?.actual_hours_overrides;
    if (overrides && typeof overrides === 'object' && Object.prototype.hasOwnProperty.call(overrides, crewId)) {
        const n = Number(overrides[crewId]);
        return Number.isFinite(n) && n >= 0 ? n : 0;
    }
    const payroll = Number(recap?.payroll_hours);
    if (recap?.payroll_hours != null && Number.isFinite(payroll) && payroll >= 0) {
        return payroll;
    }
    const fallback = Number(recap?.actual_hours);
    return Number.isFinite(fallback) && fallback >= 0 ? fallback : 0;
}

function aggregateForPayroll(recaps, crewById, adjustmentsByCrewId, period, crewDayHours = []) {
    // ── Daily paid hours (sql/057) ─────────────────────────────────────
    // A crew member's paid hours for a DATE come from their crew_day_hours
    // row (counted once), overriding the per-job chain. We map
    // (crewId, date) → hours, then pick ONE "primary" recap per (crew,date)
    // to carry that daily total so the existing per-recap summation
    // (byCrew + week bucketing) keeps working unchanged: the primary recap
    // reports the full day, the rest report 0.
    //
    // Tips are deliberately NOT daily-ized — they keep weighting by per-job
    // billing hours via effectiveHoursFor (what Jaylen approved), so the
    // multi-leg tip math below is untouched.
    const dayHoursByCrewDate = new Map(); // crewId -> Map(date -> hours)
    for (const dh of (crewDayHours || [])) {
        if (!dh?.crew_member_id || !dh?.work_date) continue;
        const h = Number(dh.computed_hours);
        if (!Number.isFinite(h) || h < 0) continue; // null/blank → per-job fallback
        if (!dayHoursByCrewDate.has(dh.crew_member_id)) dayHoursByCrewDate.set(dh.crew_member_id, new Map());
        dayHoursByCrewDate.get(dh.crew_member_id).set(dh.work_date, h);
    }
    // Primary recap per (crew,date) — earliest phase_sequence then id, among
    // that crew's recaps on the date — only where a daily row governs.
    const dailyPrimaryRecap = new Map(); // `${crewId}|${date}` -> recapId
    {
        const byKey = new Map();
        for (const r of recaps) {
            const date = r.move_date || '';
            for (const cid of (r.crew_ids || [])) {
                if (!dayHoursByCrewDate.get(cid)?.has(date)) continue;
                const key = `${cid}|${date}`;
                if (!byKey.has(key)) byKey.set(key, []);
                byKey.get(key).push(r);
            }
        }
        for (const [key, rs] of byKey) {
            rs.sort((a, b) => (a.phase_sequence || 0) - (b.phase_sequence || 0) || String(a.id).localeCompare(String(b.id)));
            dailyPrimaryRecap.set(key, rs[0].id);
        }
    }
    // Daily-aware paid hours for the PAY/OT/total path only.
    const paidHoursFor = (recap, crewId) => {
        const date = recap.move_date || '';
        const dayMap = dayHoursByCrewDate.get(crewId);
        if (dayMap && dayMap.has(date)) {
            return dailyPrimaryRecap.get(`${crewId}|${date}`) === recap.id ? dayMap.get(date) : 0;
        }
        return effectiveHoursFor(recap, crewId);
    };
    const dayGoverns = (recap, crewId) => !!dayHoursByCrewDate.get(crewId)?.has(recap.move_date || '');

    // First pass: build a per-estimate index of crew_hours so we can
    // resolve card/check tip splits across multi-leg trips (and single
    // legs, where this reduces to even-split when overrides are absent).
    //
    // Shape: estimateHoursByCrew = Map<estimate_id, Map<crew_id, total_hours>>
    // Hours summed are effective hours (override ?? actual_hours), so a
    // 1-hour deduction on a single crew member naturally shrinks their
    // share of any card/check tip attached to ANY leg of that estimate.
    const estimateHoursByCrew = new Map();
    for (const r of recaps) {
        const eid = r.estimate_id;
        if (!eid) continue;
        if (!estimateHoursByCrew.has(eid)) estimateHoursByCrew.set(eid, new Map());
        const bucket = estimateHoursByCrew.get(eid);
        for (const cid of (r.crew_ids || [])) {
            const eff = effectiveHoursFor(r, cid);
            if (eff <= 0) continue;
            bucket.set(cid, (bucket.get(cid) || 0) + eff);
        }
    }

    // Compute card/check tip splits up front, per (recap, crew_member).
    //
    // We attach `_tip_share_by_crew` onto the recap object so the
    // drill-down can render each crew member's tip share without
    // re-doing the math. Cash tips skip this — they use the stored
    // tip_splits map directly.
    //
    // Key behavior: for card/check, ALL crew who worked the estimate
    // (across every leg) get a hour-weighted share, even if they
    // weren't on the specific leg's crew_ids where the tip was entered.
    // That's how Jaylen wants multi-leg OOS tips to flow.
    //
    // CHRONOLOGICAL ORDER + tip-eligibility (Stage 4, Jaylen 2026-05-13):
    //   We process tip-bearing recaps in chronological order
    //   (move_date asc, then phase_sequence asc). For each estimate
    //   we track which crew members already received a tip share
    //   from an earlier leg, and exclude them from subsequent legs'
    //   card/check denominators. This implements "packers tipped on
    //   pack day are ineligible for move-day tip splits" without a
    //   new toggle — if a crew got ANY share earlier in the estimate
    //   (cash split or card/check share), they're out of later
    //   distributions on the same estimate. Same-leg ties (same date
    //   AND sequence) keep their natural order.
    //
    // Edge cases:
    //   • No estimate_id → fall back to even split across crew_ids on
    //                      that single recap. Single-leg local jobs
    //                      usually take this path when estimate_id is
    //                      missing for legacy reasons.
    //   • Denominator 0  → no shares allocated.
    //   • Crew member with hours in this period but not on the
    //     tip-bearing estimate → no share (they didn't work this trip).
    //   • Cross-period multi-leg estimates (rare): only crew with hours
    //     in the loaded period contribute to the denominator. A leg
    //     in an earlier period that isn't in `recaps` won't contribute,
    //     which slightly inflates this period's crew's share. We accept
    //     that imperfection for v1 — multi-leg jobs almost always sit
    //     inside a single pay period.

    // Build the chronological recap order once. Recaps without a
    // move_date sort to the front (rare; defensive).
    const recapsByDate = [...recaps].sort((a, b) => {
        const da = a?.move_date || '';
        const db = b?.move_date || '';
        if (da !== db) return da.localeCompare(db);
        return (a?.phase_sequence || 0) - (b?.phase_sequence || 0);
    });

    // Per-estimate set of crew_ids already tipped on a prior leg.
    // Updated AFTER each tip-bearing recap is processed.
    const alreadyTippedByEstimate = new Map(); // estimate_id -> Set<crew_id>
    const noteTipped = (eid, crewId) => {
        if (!eid || !crewId) return;
        let set = alreadyTippedByEstimate.get(eid);
        if (!set) { set = new Set(); alreadyTippedByEstimate.set(eid, set); }
        set.add(crewId);
    };

    for (const r of recapsByDate) {
        if (!r.tip_total || !r.tip_type) continue;
        const tipTotal = Number(r.tip_total) || 0;
        if (tipTotal <= 0) continue;

        // Cash tips don't get auto-computed shares (the lead's manual
        // tip_splits is authoritative), but they DO mark recipients as
        // already-tipped so later card/check legs skip them.
        if (r.tip_type === 'cash') {
            if (r.estimate_id) {
                for (const [cid, amt] of Object.entries(r.tip_splits || {})) {
                    if (Number(amt) > 0) noteTipped(r.estimate_id, cid);
                }
            }
            continue;
        }
        if (r.tip_type !== 'card' && r.tip_type !== 'check') continue;

        const excluded = (r.estimate_id && alreadyTippedByEstimate.get(r.estimate_id)) || new Set();

        const shares = {};
        if (r.estimate_id && estimateHoursByCrew.has(r.estimate_id)) {
            const bucket = estimateHoursByCrew.get(r.estimate_id);
            let totalEff = 0;
            for (const [cid, h] of bucket.entries()) {
                if (excluded.has(cid)) continue;
                totalEff += h;
            }
            if (totalEff > 0) {
                for (const [cid, myEff] of bucket.entries()) {
                    if (myEff <= 0) continue;
                    if (excluded.has(cid)) continue;
                    shares[cid] = tipTotal * (myEff / totalEff);
                }
            }
        } else {
            // No estimate_id — even split across crew_ids on this single recap.
            const crew = (r.crew_ids || []).filter(Boolean).filter(cid => !excluded.has(cid));
            if (crew.length > 0) {
                const each = tipTotal / crew.length;
                for (const cid of crew) shares[cid] = each;
            }
        }
        r._tip_share_by_crew = shares;

        // Record the recipients as already-tipped for downstream legs.
        for (const [cid, amt] of Object.entries(shares)) {
            if (Number(amt) > 0) noteTipped(r.estimate_id, cid);
        }
    }

    // Index of who needs a Payroll row even if they're not in the
    // tip-bearing recap's crew_ids. A crew member who worked Leg 1 of
    // a multi-leg estimate (and is in byCrew because of their Leg 1
    // hours) should pick up tip share from Leg 4 even though they're
    // not on Leg 4's crew_ids — `_tip_share_by_crew` already has them.

    const byCrew = new Map();
    for (const r of recaps) {
        for (const cid of (r.crew_ids || [])) {
            const eff = paidHoursFor(r, cid);
            // Keep zero-hour recaps when a daily row governs that date, so
            // the drill-down still shows every job of the day (the day's
            // total sits on the primary recap; the rest read 0h here).
            if (eff <= 0 && !dayGoverns(r, cid)) continue;
            if (!byCrew.has(cid)) {
                byCrew.set(cid, { crewId: cid, hours_from_recaps: 0, jobs: 0, recaps: [] });
            }
            const entry = byCrew.get(cid);
            entry.hours_from_recaps += eff;
            entry.jobs += 1;
            entry.recaps.push(r);
        }
    }

    // Pre-compute the week ranges so we can bucket each recap's hours
    // into week 1 or week 2. Falls back to "no OT" if period is missing.
    const weeks = period ? splitPayPeriodWeeks(period) : null;

    return Array.from(byCrew.values()).map(entry => {
        const member = crewById.get(entry.crewId) || {};
        const baseRate = Number(member.hourly_rate) || 0;
        const adj = adjustmentsByCrewId?.get?.(entry.crewId) || null;
        const hoursAdjustment = adj ? (Number(adj.hours_adjustment) || 0) : 0;
        const rateOverride = adj && adj.rate_override != null ? Number(adj.rate_override) : null;
        const effectiveRate = rateOverride != null ? rateOverride : baseRate;

        // Split this crew member's effective recap hours into week 1
        // and week 2. move_date is a 'YYYY-MM-DD' string; we compare
        // against the week's startIso to bucket. Recaps with no
        // move_date fall to week 1 (shouldn't happen in practice).
        // Uses effectiveHoursFor so per-crew overrides are respected.
        let week1Hours = 0;
        let week2Hours = 0;
        if (weeks) {
            const [, w2] = weeks;
            for (const r of entry.recaps) {
                const h = paidHoursFor(r, entry.crewId);
                if (h <= 0) continue;
                const md = r.move_date || '';
                if (md && md >= w2.startIso) {
                    week2Hours += h;
                } else {
                    week1Hours += h;
                }
            }
        } else {
            week1Hours = entry.hours_from_recaps;
        }

        const week1Reg = Math.min(week1Hours, OT_WEEKLY_THRESHOLD);
        const week1OT  = Math.max(0, week1Hours - OT_WEEKLY_THRESHOLD);
        const week2Reg = Math.min(week2Hours, OT_WEEKLY_THRESHOLD);
        const week2OT  = Math.max(0, week2Hours - OT_WEEKLY_THRESHOLD);

        const regularHours = week1Reg + week2Reg;
        const otHours      = week1OT + week2OT;
        const otRate       = effectiveRate * OT_MULTIPLIER;

        const payRegular    = effectiveRate > 0 ? regularHours * effectiveRate : 0;
        const payOT         = effectiveRate > 0 ? otHours * otRate : 0;
        // Hour adjustment paid at base rate, not OT-eligible by design
        // (corrections aren't a function of weekly hours bucket).
        const payAdjustment = effectiveRate > 0 ? hoursAdjustment * effectiveRate : 0;
        const payFromHours  = payRegular + payOT + payAdjustment;

        // Sum bonuses only on jobs THIS person LED. Other jobs' bonuses
        // accrue to their own lead, not to this crew member.
        let bonusTotal = 0;
        for (const r of entry.recaps) {
            if (r.leader_id !== entry.crewId) continue;
            const amt = Number(r.crew_pay_bonus_amount);
            if (isFinite(amt) && amt > 0) bonusTotal += amt;
        }

        // Tips: sum this crew member's allocation across ALL tip-bearing
        // recaps in the dataset (not just the ones in entry.recaps,
        // which is filtered to recaps where they had effective hours).
        //
        //   • Cash       — manual split entered by the lead; read from
        //                  tip_splits on each recap.
        //   • Card/check — precomputed share from _tip_share_by_crew,
        //                  which already accounts for multi-leg estimate
        //                  hours and per-crew overrides.
        //
        // Card + check feed into pay_total. Cash is informational only.
        let tipCash = 0;
        let tipCard = 0;
        let tipCheck = 0;
        for (const r of recaps) {
            if (!r.tip_total || !r.tip_type) continue;
            if (r.tip_type === 'cash') {
                const myShare = Number(r.tip_splits?.[entry.crewId]);
                if (isFinite(myShare) && myShare > 0) tipCash += myShare;
            } else if (r.tip_type === 'card') {
                const myShare = Number(r._tip_share_by_crew?.[entry.crewId]);
                if (isFinite(myShare) && myShare > 0) tipCard += myShare;
            } else if (r.tip_type === 'check') {
                const myShare = Number(r._tip_share_by_crew?.[entry.crewId]);
                if (isFinite(myShare) && myShare > 0) tipCheck += myShare;
            }
        }
        const tipPayroll = tipCard + tipCheck; // adds to pay_total
        const tipReceived = tipCash + tipPayroll; // total $ they got

        // Daily-hours metadata for the drill-down (sql/057): the per-date
        // governing map + which recap is the editable primary per date.
        const _dailyPrimaryByDate = new Map();
        for (const r of entry.recaps) {
            const date = r.move_date || '';
            const key = `${entry.crewId}|${date}`;
            if (dailyPrimaryRecap.has(key)) _dailyPrimaryByDate.set(date, dailyPrimaryRecap.get(key));
        }

        return {
            ...entry,
            name: member.name || 'Unknown',
            display_color: member.display_color || '#94a3b8',
            _dayHoursByDate: dayHoursByCrewDate.get(entry.crewId) || null,
            _dailyPrimaryByDate,
            hourly_rate: baseRate,
            gets_crew_pay: member.gets_crew_pay !== false,
            hours_adjustment: hoursAdjustment,
            rate_override: rateOverride,
            effective_rate: effectiveRate,
            ot_rate: otRate,
            total_hours: entry.hours_from_recaps + hoursAdjustment,
            week1_hours: week1Hours,
            week2_hours: week2Hours,
            regular_hours: regularHours,
            ot_hours: otHours,
            pay_regular: payRegular,
            pay_ot: payOT,
            pay_adjustment: payAdjustment,
            pay_from_hours: payFromHours,
            bonus_total: bonusTotal,
            tip_cash: tipCash,
            tip_card: tipCard,
            tip_check: tipCheck,
            tip_payroll: tipPayroll,
            tip_received: tipReceived,
            pay_total: payFromHours + bonusTotal + tipPayroll,
            active: member.active !== false,
            adjustment: adj,
        };
    }).sort((a, b) => b.total_hours - a.total_hours);
}

// ─── CSV serialization ───────────────────────────────────────────────────

// RFC 4180-ish CSV escaping — wrap fields that contain commas, quotes,
// or newlines in double-quotes and double the internal quotes. Sufficient
// for the names/numbers/dates we're emitting here.
function csvEscape(v) {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

// Build the CSV body. The Paylocity admin needs Hours + Total Pay at
// minimum; we include the breakdown columns so the source of each number
// is traceable in case of a dispute.
//   Recap Hours      — sum of actual_hours from crew lead recap submits
//   Hours Adjustment — payroll-team manual adjustment (+/-) for this period
//   Total Hours      — Recap Hours + Hours Adjustment
//   Hourly Rate      — canonical rate from crew_members
//   Rate Override    — period-specific override (blank if not set)
//   Effective Rate   — Rate Override if set, else Hourly Rate
//   Pay from Hours   — Total Hours × Effective Rate
//   Crew Pay Bonus   — sum of per-job bonuses on jobs they led this period
//   Total            — Pay from Hours + Crew Pay Bonus
function buildCsv(rows, period) {
    const header = [
        'Name',
        'Recap Hours',
        'Hours Adjustment',
        'Total Hours',
        'Regular Hours',
        'OT Hours',
        'Hourly Rate',
        'Rate Override',
        'Effective Rate',
        'OT Rate',
        'Pay Regular',
        'Pay OT',
        'Pay Adjustment',
        'Pay from Hours',
        'Crew Pay Bonus',
        // Tips: card + check feed into payroll Total; cash is tracked
        // separately for visibility but doesn't add to Total. Tips Payroll
        // is the convenience sum (card + check) since that's the number
        // Paylocity actually needs in the tip-income column.
        'Tips Card',
        'Tips Check',
        'Tips Cash',
        'Tips Payroll',
        'Total',
        'Pay Period Start',
        'Pay Period End',
    ];
    const lines = [header.map(csvEscape).join(',')];
    for (const r of rows) {
        lines.push([
            csvEscape(r.name),
            csvEscape(r.hours_from_recaps.toFixed(2)),
            csvEscape(r.hours_adjustment ? r.hours_adjustment.toFixed(2) : ''),
            csvEscape(r.total_hours.toFixed(2)),
            csvEscape(r.regular_hours.toFixed(2)),
            csvEscape(r.ot_hours ? r.ot_hours.toFixed(2) : ''),
            csvEscape(r.hourly_rate > 0 ? r.hourly_rate.toFixed(2) : ''),
            csvEscape(r.rate_override != null ? r.rate_override.toFixed(2) : ''),
            csvEscape(r.effective_rate > 0 ? r.effective_rate.toFixed(2) : ''),
            csvEscape(r.ot_hours > 0 ? r.ot_rate.toFixed(2) : ''),
            csvEscape(r.pay_regular ? r.pay_regular.toFixed(2) : ''),
            csvEscape(r.pay_ot ? r.pay_ot.toFixed(2) : ''),
            csvEscape(r.pay_adjustment ? r.pay_adjustment.toFixed(2) : ''),
            csvEscape(r.effective_rate > 0 ? r.pay_from_hours.toFixed(2) : ''),
            csvEscape(r.bonus_total ? r.bonus_total.toFixed(2) : ''),
            csvEscape(r.tip_card    ? r.tip_card.toFixed(2)    : ''),
            csvEscape(r.tip_check   ? r.tip_check.toFixed(2)   : ''),
            csvEscape(r.tip_cash    ? r.tip_cash.toFixed(2)    : ''),
            csvEscape(r.tip_payroll ? r.tip_payroll.toFixed(2) : ''),
            csvEscape(r.pay_total ? r.pay_total.toFixed(2) : ''),
            csvEscape(period.startIso),
            csvEscape(period.endIso),
        ].join(','));
    }
    return lines.join('\n') + '\n';
}

function downloadCsv(filename, csvText) {
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 100);
}

// Small pencil SVG used inline on edit-affordance buttons.
function PencilIcon({ className = '' }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11" height="11" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"
            className={className} aria-hidden="true"
        >
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
            <path d="m15 5 4 4"/>
        </svg>
    );
}

// ─── Editable per-job row (hours + crew pay bonus) ─────────────────────
//
// One drill-down row inside the per-crew expanded view. Two independent
// editable cells:
//   • Hours — writes a per-crew override on move_jobs.actual_hours_overrides
//     via saveCrewHours. Doesn't change other crew on the same job.
//   • Crew Pay Bonus — only rendered as editable when the crew member
//     this drill-down is for is the LEAD of this job AND eligibility
//     checks pass (not cancelled, not walkthrough, gets_crew_pay=true).
//     Quick-pick buttons for $25 / $50 (the standard 2-guy / 4-guy
//     amounts) sit alongside a free-form input.
//
// parentCrewId is the crew member whose expanded drill-down we're in.
// parentGetsCrewPay reflects their gets_crew_pay flag.

function EditableJobRow({ recap, clientName, parentCrewId, parentGetsCrewPay, saveCrewHours, saveBonus, showToast, parentDayHoursByDate, parentDailyPrimaryByDate, saveDayHours }) {
    // ── Daily-hours governance (sql/057) ───────────────────────────────
    // When a crew_day_hours row governs this (crew, date), paid hours are
    // a DAILY value, not per-job. We show the day total on the primary
    // recap (editable — edits the whole day for this crew member) and 0h
    // on the other jobs that day (rolled into the day total). With no
    // daily row, this falls back to the per-job override behavior below.
    const rowDate = recap.move_date || '';
    const dayGoverned = !!(parentDayHoursByDate && parentDayHoursByDate.has(rowDate));
    const dayHours = dayGoverned ? Number(parentDayHoursByDate.get(rowDate)) : null;
    const isDailyPrimary = dayGoverned && parentDailyPrimaryByDate?.get(rowDate) === recap.id;

    // Effective hours for THIS crew member on THIS job. Same precedence
    // chain as effectiveHoursFor (override → payroll_hours → actual_hours,
    // sql/052) so the editable cell always shows the number the totals
    // were computed from. Editing the hours cell here writes a per-crew
    // override (not the job-level actual_hours), so docking Brandon by
    // 1 hour doesn't change Will's hours on the same job.
    const override = recap.actual_hours_overrides && Object.prototype.hasOwnProperty.call(recap.actual_hours_overrides, parentCrewId)
        ? Number(recap.actual_hours_overrides[parentCrewId])
        : null;
    const hasOverride = override !== null && Number.isFinite(override);
    const effectiveHours = dayGoverned
        ? (isDailyPrimary ? dayHours : 0)
        : (hasOverride ? override : effectiveHoursFor(recap, parentCrewId));

    // Hours edit state
    const [editingHours, setEditingHours] = useState(false);
    const [hoursDraft, setHoursDraft] = useState(String(effectiveHours));
    const [savingHours, setSavingHours] = useState(false);

    // Bonus edit state
    const [editingBonus, setEditingBonus] = useState(false);
    const [bonusDraft, setBonusDraft] = useState(
        recap.crew_pay_bonus_amount != null ? String(recap.crew_pay_bonus_amount) : ''
    );
    const [savingBonus, setSavingBonus] = useState(false);

    const dateLabel = recap.move_date
        ? format(parseISO(recap.move_date + 'T12:00:00'), 'EEE MMM d')
        : '—';

    // Bonus eligibility: must be the lead, job not cancelled/walkthrough,
    // crew member set to receive crew pay. Owners (gets_crew_pay=false)
    // see the row but can't have a bonus entered.
    const isLead = recap.leader_id === parentCrewId;
    const isCancelled = recap.status === 'cancelled';
    const isWalkthrough = recap.type === 'WALKTHROUGH';
    const bonusEligible = isLead && !isCancelled && !isWalkthrough && parentGetsCrewPay;
    const crewSize = (recap.crew_ids || []).length;

    const commitHours = async () => {
        // Blank input → clear the override (this crew member inherits
        // the job's actual_hours along with everyone else).
        const trimmed = String(hoursDraft).trim();
        const parsed = trimmed === '' ? null : Number(trimmed);
        if (parsed !== null && (!isFinite(parsed) || parsed < 0)) {
            showToast('Hours must be a non-negative number (or blank to clear)', 'error');
            return;
        }
        setSavingHours(true);
        try {
            if (dayGoverned) {
                // Daily clock governs this date — edit the whole day's paid
                // hours for this crew member (crew_day_hours). Blank clears
                // the day row, reverting to the per-job fallback.
                await saveDayHours(parentCrewId, rowDate, parsed);
                setEditingHours(false);
                const label = parsed === null
                    ? 'Cleared daily clock hours (reverts to job hours)'
                    : `Day hours set to ${parsed.toFixed(2)}h for this crew member`;
                showToast(`${label} — ${dateLabel}`);
            } else {
                // Writes the per-crew override on this move_jobs row. Other
                // crew on the same job are unaffected. Audit log picks up
                // the change automatically (move_jobs has a trigger).
                await saveCrewHours(recap.id, parentCrewId, parsed);
                setEditingHours(false);
                const label = parsed === null
                    ? `Cleared override (back to ${Number(recap.actual_hours || 0).toFixed(2)}h job hours)`
                    : `Hours set to ${parsed.toFixed(2)}h for this crew member`;
                showToast(`${label} — ${clientName} (${dateLabel})`);
            }
        } catch (e) {
            showToast('Save failed: ' + (e?.message || e), 'error');
        } finally {
            setSavingHours(false);
        }
    };

    const commitBonus = async (overrideAmount) => {
        // overrideAmount lets quick-pick buttons commit directly without
        // first writing through the draft state — saves a click.
        const raw = overrideAmount !== undefined ? overrideAmount : bonusDraft;
        const parsed = raw === '' || raw === null ? null : Number(raw);
        if (parsed !== null && (!isFinite(parsed) || parsed < 0)) {
            showToast('Bonus must be a non-negative number (or blank)', 'error');
            return;
        }
        setSavingBonus(true);
        try {
            await saveBonus(recap.id, { amount: parsed, notes: recap.crew_pay_bonus_notes || null });
            setEditingBonus(false);
            setBonusDraft(parsed != null ? String(parsed) : '');
            if (parsed != null) {
                showToast(`Bonus set: $${parsed.toFixed(2)} for ${clientName}`);
            } else {
                showToast(`Bonus cleared for ${clientName}`);
            }
        } catch (e) {
            showToast('Save failed: ' + (e?.message || e), 'error');
        } finally {
            setSavingBonus(false);
        }
    };

    return (
        <tr className={trCls}>
            <td className={`${tdCls} text-slate-700 whitespace-nowrap`}>{dateLabel}</td>
            <td className={`${tdCls} text-slate-800 font-medium`}>{clientName}</td>
            <td className={`${tdMutedCls} tabular-nums whitespace-nowrap`}>
                {/* 12-hour format (8 AM → 4:30 PM) is more scannable than
                    24-hour. formatTime12 handles the parsing + AM/PM. */}
                {recap.actual_start_time ? formatTime12(recap.actual_start_time) : '—'}
                {' → '}
                {recap.actual_end_time ? formatTime12(recap.actual_end_time) : '—'}
            </td>
            <td className={`${tdNumCls} text-slate-600`}>
                {recap.actual_lunch_minutes != null ? `${recap.actual_lunch_minutes}m` : '—'}
            </td>

            {/* HOURS cell — per-crew effective hours; click to edit.
                Editing writes a per-crew override on this job; other
                crew on the same job are not affected. Blank input
                clears the override (back to actual_hours). When an
                override is set, we show a small amber dot + tooltip
                so the payroll team sees this row's hours diverge from
                the job's baseline. */}
            <td className={tdNumCls}>
                {editingHours ? (
                    <div className="flex items-center gap-1 justify-end">
                        <NumberField
                            size="sm"
                            step={0.5}
                            min={0}
                            value={hoursDraft}
                            onChange={v => setHoursDraft(v)}
                            disabled={savingHours}
                            autoFocus
                            placeholder={`job ${Number(recap.actual_hours || 0).toFixed(2)}`}
                            ariaLabel="Hours override"
                            className="w-36"
                        />
                        <IconButton
                            label="Save (blank = clear override)"
                            size="sm"
                            onClick={commitHours}
                            disabled={savingHours}
                        >
                            {savingHours ? '…' : '✓'}
                        </IconButton>
                        <IconButton
                            label="Cancel"
                            size="sm"
                            onClick={() => { setEditingHours(false); setHoursDraft(String(effectiveHours)); }}
                            disabled={savingHours}
                        >
                            ✕
                        </IconButton>
                    </div>
                ) : (dayGoverned && !isDailyPrimary) ? (
                    // Secondary job on a daily-clock date — paid hours are
                    // counted once on the day's primary job, so this reads 0h.
                    <span
                        className="text-slate-400 inline-flex items-center gap-1 justify-end"
                        title="Paid hours for this crew member are counted on their first job of the day (daily clock). Edit the day total there."
                    >
                        —
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400" aria-hidden="true" />
                    </span>
                ) : (
                    <button
                        onClick={() => setEditingHours(true)}
                        className="font-bold text-slate-900 hover:underline decoration-dotted underline-offset-2 inline-flex items-center gap-1 group"
                        title={dayGoverned
                            ? `Daily clock hours for ${dateLabel} — editing changes this crew member's whole day. Clear to revert to job hours.`
                            : hasOverride
                            ? `Override for this crew member only — job baseline is ${Number(recap.actual_hours || 0).toFixed(2)}h. Click to edit; clear to revert.`
                            : `Click to edit. Saves as a per-crew override; doesn't change other crew on this job.`}
                    >
                        {effectiveHours.toFixed(2)}h
                        {(hasOverride || dayGoverned) && (
                            <span
                                className={`inline-block w-1.5 h-1.5 rounded-full ${dayGoverned ? 'bg-violet-500' : 'bg-amber-500'}`}
                                aria-hidden="true"
                                title={dayGoverned ? 'Daily clock hours' : 'Per-crew override'}
                            />
                        )}
                        <PencilIcon className="text-slate-400 group-hover:text-red-600 transition-colors" />
                    </button>
                )}
            </td>

            {/* LEAD cell — small green "Lead" pill if this crew member was
                the designated leader on this job, otherwise blank. The
                Lead column makes role-status visible at a glance instead
                of forcing the reader to hover over the Crew Pay column
                to infer it. */}
            <td className={`${tdCls} text-center`}>
                {isLead ? (
                    <StatusPill label="Lead" color="#059669" />
                ) : (
                    <span className="text-slate-300" aria-hidden="true">—</span>
                )}
            </td>

            {/* CREW PAY cell — only editable when eligible (lead + not
                cancelled/walkthrough + gets_crew_pay). Quick-pick chips
                for $25 / $50 + free-form input. Shows crew size badge
                (2-guy / 4-guy / etc.) to nudge which quick-pick applies.
                For non-lead rows, show "N/A" explicitly (clearer than
                "—") since the Lead column already shows the role state.
                For owner-style gets_crew_pay=false: show the "Excluded"
                badge so the reason is visible at a glance. */}
            <td className={tdNumCls}>
                {!bonusEligible ? (
                    isLead && !isCancelled && !isWalkthrough && !parentGetsCrewPay ? (
                        <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 whitespace-nowrap"
                            title="This crew member is set to not receive crew pay (e.g. owner). Toggle 'Receives Crew Pay' on the Crew page to enable."
                        >
                            Excluded
                        </span>
                    ) : !isLead ? (
                        <span className="text-slate-400 text-[12px]" title="Crew pay accrues only to the lead">
                            N/A
                        </span>
                    ) : (
                        <span
                            className="text-slate-300"
                            title={isCancelled ? 'Cancelled — no crew pay' : isWalkthrough ? 'Walkthrough — no crew pay' : 'Not eligible'}
                        >—</span>
                    )
                ) : editingBonus ? (
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => commitBonus(25)}
                            disabled={savingBonus}
                            title="Quick-pick: $25 (2-guy crew)"
                        >$25</Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => commitBonus(50)}
                            disabled={savingBonus}
                            title="Quick-pick: $50 (4-guy crew)"
                        >$50</Button>
                        <NumberField
                            size="sm"
                            step={5}
                            min={0}
                            prefix="$"
                            value={bonusDraft}
                            onChange={v => setBonusDraft(v)}
                            disabled={savingBonus}
                            autoFocus
                            placeholder="Other"
                            ariaLabel="Bonus amount"
                            className="w-32"
                        />
                        <IconButton
                            label="Save"
                            size="sm"
                            onClick={() => commitBonus()}
                            disabled={savingBonus}
                        >
                            {savingBonus ? '…' : '✓'}
                        </IconButton>
                        <IconButton
                            label="Cancel"
                            size="sm"
                            onClick={() => { setEditingBonus(false); setBonusDraft(recap.crew_pay_bonus_amount != null ? String(recap.crew_pay_bonus_amount) : ''); }}
                            disabled={savingBonus}
                        >
                            ✕
                        </IconButton>
                    </div>
                ) : (
                    <button
                        onClick={() => setEditingBonus(true)}
                        className={`inline-flex items-center gap-1.5 group ${recap.crew_pay_bonus_amount != null ? 'font-bold text-slate-900' : 'text-slate-400 italic'} hover:underline decoration-dotted underline-offset-2`}
                        title={recap.type === 'LONG_DISTANCE' ? 'Long-distance job — crew pay is non-standardized, enter manually' : `${crewSize}-guy crew — click to set bonus`}
                    >
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 tabular-nums whitespace-nowrap">
                            {recap.type === 'LONG_DISTANCE' ? 'Long-dist' : `${crewSize}-guy`}
                        </span>
                        {recap.crew_pay_bonus_amount != null
                            ? `$${Number(recap.crew_pay_bonus_amount).toFixed(2)}`
                            : 'Set'}
                        <PencilIcon className="text-slate-400 group-hover:text-red-600 transition-colors" />
                    </button>
                )}
            </td>

            {/* TIP cell — this crew member's allocated share of the tip
                on this job. Cash splits are the lead's manual entry
                (tip_splits map). Card/check shares are computed by
                aggregateForPayroll based on hours across the whole
                estimate, then attached as _tip_share_by_crew. */}
            <td className={tdNumCls}>
                {(() => {
                    if (!recap.tip_total || !recap.tip_type) {
                        return <span className="text-slate-300">—</span>;
                    }
                    const myShare = recap.tip_type === 'cash'
                        ? Number(recap.tip_splits?.[parentCrewId])
                        : Number(recap._tip_share_by_crew?.[parentCrewId]);
                    if (!isFinite(myShare) || myShare <= 0) {
                        return <span className="text-slate-300">—</span>;
                    }
                    const isPayroll = recap.tip_type === 'card' || recap.tip_type === 'check';
                    return (
                        <span
                            className="inline-flex items-center gap-1"
                            title={`Total tip on this job: $${Number(recap.tip_total).toFixed(2)} (${recap.tip_type}) · This crew member's share: $${myShare.toFixed(2)}${isPayroll ? ' — auto-computed by hours; flows into pay total' : ' — cash, taken home on-site'}`}
                        >
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                recap.tip_type === 'cash'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : recap.tip_type === 'card'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-violet-100 text-violet-700'
                            }`}>
                                {recap.tip_type}
                            </span>
                            <span className={isPayroll ? 'font-bold text-emerald-700' : 'text-slate-700'}>
                                ${myShare.toFixed(2)}
                            </span>
                        </span>
                    );
                })()}
            </td>
        </tr>
    );
}

// ─── Per-crew rate adjustment panel ─────────────────────────────────────
//
// Compact one-liner by default; click Add/Edit to expand into a form.
// Lives at the bottom of the expanded drill-down — per-job hours and
// crew pay are the primary data; rate override is an edge-case knob for
// mid-period raises etc. The hours adjustment field that lived here
// previously was removed since hours are editable per-job already.
//
// Empty rate_override = use crew_members.hourly_rate. Notes carry the
// reason ("raise effective May 10") and aren't required at the DB level.

function AdjustmentsPanel({ row, onSave, showToast }) {
    const [editing, setEditing] = useState(false);
    const [rateDraft, setRateDraft] = useState(row.rate_override != null ? String(row.rate_override) : '');
    const [notesDraft, setNotesDraft] = useState(row.adjustment?.notes || '');
    const [saving, setSaving] = useState(false);

    const hasAdjustment = row.rate_override != null || !!row.adjustment?.notes;

    const reset = () => {
        setRateDraft(row.rate_override != null ? String(row.rate_override) : '');
        setNotesDraft(row.adjustment?.notes || '');
    };

    const commit = async () => {
        if (rateDraft !== '' && !isFinite(Number(rateDraft))) {
            showToast('Rate override must be a number or blank', 'error');
            return;
        }
        setSaving(true);
        try {
            await onSave(row.crewId, {
                // hoursAdjustment stays at 0 — the DB column still exists
                // but we don't expose it (hours are editable per-job now).
                hoursAdjustment: 0,
                rateOverride: rateDraft === '' ? null : Number(rateDraft),
                notes: notesDraft || null,
            });
            setEditing(false);
            showToast(`Rate adjustment saved for ${row.name}`);
        } catch (e) {
            showToast('Save failed: ' + (e?.message || e), 'error');
        } finally {
            setSaving(false);
        }
    };

    // Compact view mode — one line, no chrome. Renders an inline label +
    // current value (or "none") + a small button to open the form.
    if (!editing) {
        return (
            <div className="flex items-center justify-between gap-3 px-2 py-2 text-[12px]">
                <div className="min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-2">
                        Rate adjustment
                    </span>
                    {hasAdjustment ? (
                        <span className="text-slate-700">
                            {row.rate_override != null && (
                                <>
                                    <span className="font-semibold text-slate-900">${row.rate_override.toFixed(2)}/hr</span>
                                    <span className="text-slate-400 ml-2">(canonical: ${row.hourly_rate.toFixed(2)})</span>
                                </>
                            )}
                            {row.adjustment?.notes && (
                                <span className="text-slate-500 italic ml-2">
                                    — {row.adjustment.notes}
                                </span>
                            )}
                        </span>
                    ) : (
                        <span className="text-slate-400 italic">none</span>
                    )}
                </div>
                <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setEditing(true)}
                >
                    {hasAdjustment ? 'Edit' : 'Add'}
                </Button>
            </div>
        );
    }

    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-2">
                Editing rate adjustment — {row.name}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                <div>
                    <label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider block mb-0.5">
                        Rate override ($/hr)
                    </label>
                    <NumberField
                        size="sm"
                        step={5}
                        min={0}
                        prefix="$"
                        value={rateDraft}
                        onChange={v => setRateDraft(v)}
                        disabled={saving}
                        placeholder={`canonical ${row.hourly_rate.toFixed(2)}`}
                        ariaLabel="Rate override per hour"
                        className="w-full"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider block mb-0.5">
                        Notes (reason)
                    </label>
                    <input
                        type="text"
                        value={notesDraft}
                        onChange={e => setNotesDraft(e.target.value)}
                        disabled={saving}
                        placeholder="e.g. raise effective May 10"
                        className="w-full text-[12px] border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500/30"
                    />
                </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-2">
                <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => { reset(); setEditing(false); }}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={commit}
                    loading={saving}
                >
                    Save
                </Button>
            </div>
        </div>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────

export default function PayrollPage() {
    const { crew, clients, jobs } = useStore();
    const { show: showToast } = useToast();

    const [periodOffset, setPeriodOffset] = useState(0); // 0 = current, -1 = previous, etc.
    const [recaps, setRecaps] = useState(null);
    const [crewDayHours, setCrewDayHours] = useState([]); // sql/057 daily paid hours
    const [adjustments, setAdjustments] = useState([]);
    const [error, setError] = useState(null);
    const [expandedCrewId, setExpandedCrewId] = useState(null);

    const period = useMemo(() => shiftPayPeriod(payPeriodFor(new Date()), periodOffset), [periodOffset]);
    const periodLabel = useMemo(() => formatPayPeriod(period), [period]);
    const periodIso = useMemo(() => payPeriodIsoRange(period), [period]);

    const crewById = useMemo(() => {
        const m = new Map();
        (crew || []).forEach(c => m.set(c.id, c));
        return m;
    }, [crew]);
    const clientsById = useMemo(() => {
        const m = new Map();
        (clients || []).forEach(c => m.set(c.id, c));
        return m;
    }, [clients]);

    // Fetch recaps for the selected period. Refetches when periodOffset
    // changes OR when `jobs` changes — the latter handles the optimistic
    // refresh after an inline hours edit (store updates jobs, we re-aggregate).
    useEffect(() => {
        let cancelled = false;
        setRecaps(null);
        setError(null);
        Promise.all([
            db.getRecaps({ since: periodIso.since, until: periodIso.until }),
            db.getPayrollAdjustments({ periodStartIso: period.startIso }),
            // Daily paid hours (sql/057). Non-fatal — pre-057 deploys 404
            // the table; we degrade to the per-job fallback chain.
            db.getCrewDayHours({ since: periodIso.since, until: periodIso.until }).catch(e => {
                console.warn('PayrollPage: getCrewDayHours failed (probably pre-057):', e?.message || e);
                return [];
            }),
        ])
            .then(([recapData, adjData, dayHoursData]) => {
                if (cancelled) return;
                setRecaps(recapData);
                setAdjustments(adjData);
                setCrewDayHours(Array.isArray(dayHoursData) ? dayHoursData : []);
            })
            .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load payroll data'); });
        return () => { cancelled = true; };
        // jobs included in deps so an edit to actual_hours triggers refetch
        // and the table re-aggregates with the new value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodIso.since, periodIso.until, period.startIso, jobs]);

    const adjustmentsByCrewId = useMemo(() => {
        const m = new Map();
        (adjustments || []).forEach(a => m.set(a.crew_member_id, a));
        return m;
    }, [adjustments]);

    const rows = useMemo(() => {
        if (!Array.isArray(recaps)) return [];
        return aggregateForPayroll(recaps, crewById, adjustmentsByCrewId, period, crewDayHours);
    }, [recaps, crewById, adjustmentsByCrewId, period, crewDayHours]);

    const totalHours = useMemo(() => rows.reduce((acc, r) => acc + r.total_hours, 0), [rows]);
    const totalPay   = useMemo(() => rows.reduce((acc, r) => acc + r.pay_total, 0), [rows]);
    const totalBonus = useMemo(() => rows.reduce((acc, r) => acc + r.bonus_total, 0), [rows]);
    const totalOT    = useMemo(() => rows.reduce((acc, r) => acc + r.pay_ot, 0), [rows]);
    const totalOTHours = useMemo(() => rows.reduce((acc, r) => acc + r.ot_hours, 0), [rows]);
    const totalTipPayroll = useMemo(() => rows.reduce((acc, r) => acc + r.tip_payroll, 0), [rows]);
    const totalTipCash    = useMemo(() => rows.reduce((acc, r) => acc + r.tip_cash, 0), [rows]);
    const crewCount  = rows.length;

    const handleExport = () => {
        if (rows.length === 0) {
            showToast('No hours to export for this period', 'error');
            return;
        }
        const filename = `bbm-payroll-${payPeriodSlug(period)}.csv`;
        downloadCsv(filename, buildCsv(rows, period));
        showToast(`Exported ${rows.length} crew members to ${filename}`);
    };

    // Per-crew-per-job hours override. Writes through to
    // move_jobs.actual_hours_overrides[crew_member_id]. Other crew on
    // the same job are unaffected — this is exactly the "Brandon was
    // late, dock his hour" surface. Local recap state is patched in
    // place so the table re-aggregates immediately without a refetch.
    const handleSaveCrewHours = async (jobId, crewId, hours) => {
        const updated = await db.saveJobCrewHours(jobId, crewId, hours);
        setRecaps(prev => Array.isArray(prev) ? prev.map(r => r.id === jobId ? {
            ...r,
            actual_hours_overrides: updated.actual_hours_overrides || {},
        } : r) : prev);
        return updated;
    };

    // Admin+ edit of a crew member's DAILY paid hours (sql/057). Used by
    // the drill-down when a crew_day_hours row governs that (crew, date).
    // Blank clears the row (reverts to the per-job fallback). We patch the
    // local crewDayHours array so the table re-aggregates immediately.
    const handleSaveDayHours = async (crewId, date, hours) => {
        const updated = await db.saveCrewDayHoursValue(crewId, date, hours);
        setCrewDayHours(prev => {
            const arr = (Array.isArray(prev) ? prev : []).filter(
                r => !(r.crew_member_id === crewId && r.work_date === date),
            );
            if (updated) arr.push(updated);
            return arr;
        });
        return updated;
    };

    // Save a per-job crew pay bonus. Writes the bonus_amount and notes
    // directly to the move_jobs row via db.saveCrewPayBonus, then refresh
    // the local recap state so the drill-down updates without a full
    // page reload.
    const handleSaveBonus = async (jobId, { amount, notes }) => {
        const updated = await db.saveCrewPayBonus(jobId, { amount, notes });
        // Splice the updated row's bonus fields back into our local
        // recap array so React re-renders with the new value immediately.
        setRecaps(prev => Array.isArray(prev) ? prev.map(r => r.id === jobId ? {
            ...r,
            crew_pay_bonus_amount: updated.crew_pay_bonus_amount,
            crew_pay_bonus_notes: updated.crew_pay_bonus_notes,
        } : r) : prev);
        return updated;
    };

    // Save the per-(crew, period) adjustment row. Upserts via the unique
    // (crew_member_id, period_start) constraint. We refresh the
    // adjustments state directly so the row's pay total updates without
    // a network round-trip.
    const handleSaveAdjustment = async (crewMemberId, { hoursAdjustment, rateOverride, notes }) => {
        const saved = await db.savePayrollAdjustment({
            crewMemberId,
            periodStartIso: period.startIso,
            hoursAdjustment,
            rateOverride,
            notes,
        });
        setAdjustments(prev => {
            const others = (prev || []).filter(a => a.crew_member_id !== crewMemberId);
            return [...others, saved];
        });
        return saved;
    };

    const summary = recaps === null
        ? periodLabel
        : `${crewCount} crew · ${totalHours.toFixed(1)} hrs · $${totalPay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} this period`;

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Payroll"
                summary={summary}
                help={<>
                    <p>Bi-weekly hours rollup with editable values and CSV export for Paylocity. Pay periods run Saturday through Friday × 2 (anchor: May 2, 2026). Click a crew member to edit per-job hours, crew pay bonuses and rate adjustments for the period.</p>
                    <p className="mt-2">Times shown in Eastern Time (EST). Direct deposit processes Sunday after period close; funds land Tuesday. CSV export is formatted for paste-into-Paylocity workflows.</p>
                </>}
                actions={
                    <Button
                        variant="secondary"
                        onClick={handleExport}
                        disabled={rows.length === 0}
                    >
                        Export CSV
                    </Button>
                }
            />

            {/* Pay period stepper */}
            <Toolbar>
                <PeriodStepper
                    label={periodLabel}
                    sublabel="Pay period"
                    onPrev={() => setPeriodOffset(o => o - 1)}
                    onNext={() => setPeriodOffset(o => Math.min(0, o + 1))}
                    onToday={() => setPeriodOffset(0)}
                    todayLabel="Current period"
                />
            </Toolbar>

            {/* Summary tiles — seven: payroll / hours / OT / bonus / tips / recaps / crew.
                Auto-fit avoids the awkward {4,3} wrap at md and the lonely lone tile
                at 2-col mobile. Tiles flow into as many columns as fit at ~150px each. */}
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total payroll</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{totalPay > 0 ? `$${totalPay.toFixed(2)}` : '—'}</div>
                    <div className="text-[11px] text-slate-400">
                        regular + OT + bonuses + tips
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total hours</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{totalHours.toFixed(2)}</div>
                    <div className="text-[11px] text-slate-400">across all crew</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Overtime</div>
                    <div className={`text-2xl font-bold tabular-nums ${totalOTHours > 0 ? 'text-rose-700' : 'text-slate-900'}`}>
                        {totalOTHours > 0 ? `${totalOTHours.toFixed(2)}h` : '—'}
                    </div>
                    <div className="text-[11px] text-slate-400">
                        {totalOT > 0 ? `+$${totalOT.toFixed(2)} OT pay` : '>40h/wk'}
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Crew pay bonus</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{totalBonus > 0 ? `$${totalBonus.toFixed(2)}` : '—'}</div>
                    <div className="text-[11px] text-slate-400">leads only, this period</div>
                </div>
                {/* Tips tile — card + check feed into payroll; cash shown
                    inline so the office can see the full picture without
                    confusing it with what's owed. */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tips</div>
                    <div className={`text-2xl font-bold tabular-nums ${totalTipPayroll > 0 ? 'text-emerald-700' : 'text-slate-900'}`}>
                        {totalTipPayroll > 0 ? `+$${totalTipPayroll.toFixed(2)}` : '—'}
                    </div>
                    <div className="text-[11px] text-slate-400" title="Card + check tips add to payroll. Cash tips were taken home on-site.">
                        {totalTipCash > 0
                            ? `card + check (cash $${totalTipCash.toFixed(2)} tracked)`
                            : 'card + check, this period'}
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Recaps</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{recaps?.length || 0}</div>
                    <div className="text-[11px] text-slate-400">jobs in period</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Crew</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{crewCount}</div>
                    <div className="text-[11px] text-slate-400">with logged hours</div>
                </div>
            </div>

            {/* Table — ledger recipe, see BBM Context for claude/lists.md §3 */}
            <div className={tableWrapCls}>
                {recaps === null && <RowSkeleton rows={5} />}
                {error && (
                    <div className="px-4 py-3 text-sm font-semibold text-red-600 bg-red-50 border-b border-red-200">{error}</div>
                )}
                {Array.isArray(recaps) && rows.length === 0 && !error && (
                    <EmptyState
                        icon={<IconDollarSign size={28} />}
                        title={`No recaps in ${periodLabel}`}
                        hint="Pick a different period or wait for crew leads to submit."
                        action={periodOffset !== 0
                            ? <Button variant="tertiary" size="sm" onClick={() => setPeriodOffset(0)}>Current period</Button>
                            : <Button variant="secondary" size="sm" onClick={() => setPeriodOffset(-1)}>Previous period</Button>}
                        className="border-0 rounded-none"
                    />
                )}
                {rows.length > 0 && (
                    <table className={tableCls}>
                        <thead>
                            <tr>
                                <th className={thCls}>Crew member</th>
                                <th className={`${thNumCls} w-28`}>Hours</th>
                                <th className={`${thNumCls} w-20`}>OT</th>
                                <th className={`${thNumCls} w-24`}>Rate</th>
                                <th className={`${thNumCls} w-24`}>Bonus</th>
                                <th className={`${thNumCls} w-28`}>Tips</th>
                                <th className={`${thNumCls} w-28`}>Total</th>
                                <th className={`${thNumCls} w-16`}>Jobs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const isExpanded = expandedCrewId === row.crewId;
                                return (
                                    <React.Fragment key={row.crewId}>
                                        <tr
                                            onClick={() => setExpandedCrewId(isExpanded ? null : row.crewId)}
                                            // When expanded, this row uses the shared open-row language
                                            // (red edge + faint tint, lists.md §3) and the drill-down
                                            // below sits in the expansion row.
                                            className={`${trClickCls} ${isExpanded ? trOpenCls : ''}`}
                                        >
                                            <td className={tdCls}>
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className="text-xs font-bold px-2 py-0.5 rounded-md text-white"
                                                        style={{ background: row.display_color }}
                                                    >
                                                        {row.name}
                                                    </span>
                                                    {!row.active && (
                                                        <span className="text-[10px] text-slate-400 italic">inactive</span>
                                                    )}
                                                    <span aria-hidden="true" className="text-slate-300 text-xs ml-1">
                                                        {isExpanded ? '▾' : '▸'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className={`${tdNumCls} font-bold text-slate-900`}>
                                                {row.total_hours.toFixed(2)}
                                                {row.hours_adjustment !== 0 && (
                                                    <span
                                                        className={`block text-[10px] font-normal tabular-nums ${row.hours_adjustment > 0 ? 'text-emerald-600' : 'text-rose-600'}`}
                                                        title={`Recap: ${row.hours_from_recaps.toFixed(2)}h · adjustment: ${row.hours_adjustment > 0 ? '+' : ''}${row.hours_adjustment.toFixed(2)}h`}
                                                    >
                                                        {row.hours_from_recaps.toFixed(2)} {row.hours_adjustment > 0 ? '+' : ''}{row.hours_adjustment.toFixed(2)}
                                                    </span>
                                                )}
                                            </td>
                                            {/* OT cell — rose when > 0 so it visually pops.
                                                Tooltip explains the per-week breakdown so
                                                payroll can see WHICH week triggered OT. */}
                                            <td className={tdNumCls}>
                                                {row.ot_hours > 0 ? (
                                                    <span
                                                        className="font-bold text-rose-700"
                                                        title={`Week 1: ${row.week1_hours.toFixed(2)}h · Week 2: ${row.week2_hours.toFixed(2)}h · OT rate: $${row.ot_rate.toFixed(2)}/hr (${OT_MULTIPLIER}× base)`}
                                                    >
                                                        {row.ot_hours.toFixed(2)}
                                                        <span className="block text-[10px] font-normal text-rose-500">
                                                            +${row.pay_ot.toFixed(2)}
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className={`${tdNumCls} text-slate-700`}>
                                                {row.effective_rate > 0
                                                    ? <>
                                                        ${row.effective_rate.toFixed(2)}
                                                        {row.rate_override != null && (
                                                            <span className="block text-[10px] text-amber-600" title={`Override for this period — canonical $${row.hourly_rate.toFixed(2)}/hr`}>
                                                                override
                                                            </span>
                                                        )}
                                                    </>
                                                    : <span className="text-rose-500">unset</span>}
                                            </td>
                                            <td className={`${tdNumCls} text-slate-700`}>
                                                {row.bonus_total > 0
                                                    ? `$${row.bonus_total.toFixed(2)}`
                                                    : <span className="text-slate-300">—</span>}
                                            </td>
                                            {/* TIPS cell — show payroll-affecting amount (card+check)
                                                as the headline value; cash is sub-labeled below since
                                                it's informational only and tracked off-payroll. */}
                                            <td className={tdNumCls}>
                                                {row.tip_received > 0 ? (
                                                    <span
                                                        title={`Card $${row.tip_card.toFixed(2)} · Check $${row.tip_check.toFixed(2)} · Cash $${row.tip_cash.toFixed(2)} (cash tracked but doesn't flow through payroll)`}
                                                    >
                                                        {row.tip_payroll > 0 ? (
                                                            <span className="font-bold text-emerald-700">+${row.tip_payroll.toFixed(2)}</span>
                                                        ) : (
                                                            <span className="text-slate-400">—</span>
                                                        )}
                                                        {row.tip_cash > 0 && (
                                                            <span className="block text-[10px] font-normal text-slate-500">
                                                                cash ${row.tip_cash.toFixed(2)}
                                                            </span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className={`${tdNumCls} font-bold text-slate-900`}>
                                                {row.effective_rate > 0 || row.bonus_total > 0 || row.tip_payroll > 0
                                                    ? `$${row.pay_total.toFixed(2)}`
                                                    : '—'}
                                            </td>
                                            <td className={`${tdNumCls} text-slate-500`}>{row.jobs}</td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className={trExpandCls}>
                                                <td colSpan={8} className="p-0">
                                                    <div className={tdExpandCls}>
                                                        {/* Weekly OT breakdown — gives payroll visibility
                                                            into WHICH week pushed the crew member over 40h. */}
                                                        <div className="mb-3 flex flex-wrap gap-3 items-center text-[12px] text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2">
                                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                                                Weekly hours
                                                            </span>
                                                            <span className="tabular-nums">
                                                                Week 1: <strong>{row.week1_hours.toFixed(2)}h</strong>
                                                                {row.week1_hours > OT_WEEKLY_THRESHOLD && (
                                                                    <span className="text-rose-600 ml-1">({(row.week1_hours - OT_WEEKLY_THRESHOLD).toFixed(2)}h OT)</span>
                                                                )}
                                                            </span>
                                                            <span className="text-slate-300">·</span>
                                                            <span className="tabular-nums">
                                                                Week 2: <strong>{row.week2_hours.toFixed(2)}h</strong>
                                                                {row.week2_hours > OT_WEEKLY_THRESHOLD && (
                                                                    <span className="text-rose-600 ml-1">({(row.week2_hours - OT_WEEKLY_THRESHOLD).toFixed(2)}h OT)</span>
                                                                )}
                                                            </span>
                                                            {row.ot_hours > 0 && (
                                                                <>
                                                                    <span className="text-slate-300">·</span>
                                                                    <span className="tabular-nums text-rose-700">
                                                                        OT rate: <strong>${row.ot_rate.toFixed(2)}/hr</strong> ({OT_MULTIPLIER}× base)
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                        {/* Drill-down table — same ledger th/td classes (lists.md §3), smaller text */}
                                                        <table className="w-full text-[12px] bg-white rounded-lg border border-slate-200">
                                                            <thead>
                                                                <tr>
                                                                    <th className={thCls}>Date</th>
                                                                    <th className={thCls}>Client</th>
                                                                    <th className={thCls}>Times</th>
                                                                    <th className={thNumCls}>Lunch</th>
                                                                    <th className={thNumCls}>Hours</th>
                                                                    <th className={`${thCls} text-center w-16`}>Lead</th>
                                                                    <th className={thNumCls}>Crew Pay</th>
                                                                    <th className={thNumCls}>Tip</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {row.recaps
                                                                    .slice()
                                                                    .sort((a, b) => (b.move_date || '').localeCompare(a.move_date || ''))
                                                                    .map(r => (
                                                                        <EditableJobRow
                                                                            key={r.id}
                                                                            recap={r}
                                                                            clientName={(r.clients?.name) || clientsById.get(r.client_id)?.name || '—'}
                                                                            parentCrewId={row.crewId}
                                                                            parentGetsCrewPay={row.gets_crew_pay}
                                                                            saveCrewHours={handleSaveCrewHours}
                                                                            saveBonus={handleSaveBonus}
                                                                            showToast={showToast}
                                                                            parentDayHoursByDate={row._dayHoursByDate}
                                                                            parentDailyPrimaryByDate={row._dailyPrimaryByDate}
                                                                            saveDayHours={handleSaveDayHours}
                                                                        />
                                                                    ))}
                                                            </tbody>
                                                        </table>
                                                        {/* Rate adjustment — collapsed by default at the bottom
                                                            of the drill-down. Edge-case knob for mid-period
                                                            raises etc. Per-job hours and crew pay above are
                                                            the primary edits. */}
                                                        <div className="mt-3 border-t border-slate-200">
                                                            <AdjustmentsPanel
                                                                row={row}
                                                                onSave={handleSaveAdjustment}
                                                                showToast={showToast}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
